import {
  HighSignificanceIcon,
  LightBulbIcon,
  LowSignificanceIcon,
  MagnifyingGlassIcon,
  ModerateSignificanceIcon,
  StartLessonsIcon
} from './icons';
import { HandsHoldingPlantIcon } from '@magoosh/lib/icons';
import style from './style.module.scss';
import { SubjectTagStat } from './types';
import { DefaultButton, PrimaryButton } from '@magoosh/lib/button';
import { LightBulbPlantIcon, WateringSeedlingIcon } from '@magoosh/lib/icons';
import ProgressBar from '@magoosh/lib/progress_bar';
import cx from 'classnames';
import { paths } from 'config/path_helpers';
import moment from 'moment';
import * as React from 'react';
import { Col, Row } from 'react-bootstrap';
import { toSentence } from 'utilities/formatting';

interface Props {
  examSections: Record<string, SubjectTagStat[]>;
  onClickAnswers: () => void;
  lastActivityAt: number;
  resultingStudyPlanFocus?: {
    math: string[];
    verbal: string[];
  };
}

const PerformanceTabGre = (props: Props) => {
  const shouldShowCTA = props.resultingStudyPlanFocus;

  const viewAnswersLink = (
    <a style={{ color: style.brandSecondary }} onClick={props.onClickAnswers}>
      <strong>View Your Answers</strong>
    </a>
  );

  const renderMath = () => {
    const mathTags = props.examSections.math;

    if (!mathTags) {
      return (
        <>
          <h3>
            <strong>Math Section</strong>
          </h3>
          <NoPerformanceData />
        </>
      );
    }

    const mathHighlight = mathTags.filter((tag) => tag.highlightArea)[0];
    const mathImprovement = mathTags.filter((tag) => tag.improvementArea)[0];

    return (
      <React.Fragment>
        <h3>
          <strong>Math Section</strong>
        </h3>
        {mathHighlight && mathImprovement && (
          <div className={cx(style.cardRow, 'u-margin-T-m', 'u-margin-B-xl')}>
            <div>
              <Row>
                <Col sm={3} className="text-center">
                  <LightBulbPlantIcon style={{ fill: style.moderateSignificance }} height={150} width={100} />
                </Col>
                <Col sm={9} className={style.highlightContent}>
                  <span>HIGHLIGHT</span>
                  <strong>{mathHighlight.name}</strong>
                  <span>
                    You answered {mathHighlight.percentCorrect}% of questions on this subject correctly. While
                    you focus on other areas of improvement, make sure to keep reviewing this area so it
                    remains a strength.
                  </span>
                </Col>
              </Row>
            </div>
            {!shouldShowCTA ? (
              <div>
                <Row>
                  <Col sm={3} className="text-center">
                    <WateringSeedlingIcon style={{ fill: style.brandPrimary }} height={150} width={100} />
                  </Col>
                  <Col sm={9} className={style.highlightContent}>
                    <span>ROOM FOR IMPROVEMENT</span>
                    <strong>{mathImprovement.name}</strong>
                    <span>
                      You answered {mathImprovement.percentCorrect}% of questions on this subject correctly.
                      Focusing on this subject will give you the best opportunity to improve your score.
                    </span>
                  </Col>
                </Row>
              </div>
            ) : (
              renderStudyPlanCTA('math')
            )}
          </div>
        )}

        <h4>
          <strong>Your score breakdown</strong>
        </h4>
        <p className="text-gray">
          Review how you did on each topic, sorted by how significant it is on the GRE.
        </p>
        <Row>
          <Col md={3} sm={12}>
            <HighSignificanceIcon style={{ fill: style.highSignificance, height: '17px', width: '22px' }} />
            <h5>
              <strong>High Significance</strong>
            </h5>
            <p className="text-gray-light">
              These subjects appear most frequently on the test and have the biggest impact on your score.
            </p>
            {viewAnswersLink}
          </Col>
          <Col md={9} sm={12}>
            {mathTags
              .filter((tag) => tag.significanceBucket === 'high')
              .map((tag) => renderSubjectTagRow(tag, style.highSignificance))}
          </Col>
        </Row>
        <hr />
        <Row>
          <Col md={3} sm={12}>
            <ModerateSignificanceIcon
              style={{ fill: style.moderateSignificance, height: '17px', width: '22px' }}
            />
            <h5>
              <strong>Moderate Significance</strong>
            </h5>
            <p className="text-gray-light">
              While these subjects appear less frequently on the test, they are still important for your
              overall score.
            </p>
            {viewAnswersLink}
          </Col>
          <Col md={9} sm={12}>
            {mathTags
              .filter((tag) => tag.significanceBucket === 'moderate')
              .map((tag) => renderSubjectTagRow(tag, style.moderateSignificance))}
          </Col>
        </Row>
        <hr />
        <Row>
          <Col md={3} sm={12}>
            <LowSignificanceIcon style={{ fill: style.lowSignificance, height: '17px', width: '22px' }} />
            <h5>
              <strong>Low Significance</strong>
            </h5>
            <p className="text-gray-light">
              These subjects appear least frequently. Improving in these subjects is less important for your
              overall score.
            </p>
            {viewAnswersLink}
          </Col>
          <Col md={9} sm={12}>
            {mathTags
              .filter((tag) => tag.significanceBucket === 'low')
              .map((tag) => renderSubjectTagRow(tag, style.lowSignificance))}
          </Col>
        </Row>
      </React.Fragment>
    );
  };

  const renderVerbal = () => {
    const verbalTags = props.examSections.verbal;
    if (!verbalTags) {
      return (
        <>
          <h3>
            <strong>Verbal Section</strong>
          </h3>
          <NoPerformanceData />
        </>
      );
    }

    const verbalHighlight = verbalTags.filter((tag) => tag.highlightArea)[0];
    const verbalImprovement = verbalTags.filter((tag) => tag.improvementArea)[0];

    return (
      <React.Fragment>
        <h3>
          <strong>Verbal Section</strong>
        </h3>
        {verbalHighlight && verbalImprovement && (
          <div className={cx(style.cardRow, 'u-margin-T-m', 'u-margin-B-xl')}>
            <div>
              <Row>
                <Col sm={3} className="text-center">
                  <LightBulbPlantIcon style={{ fill: style.moderateSignificance }} height={150} width={100} />
                </Col>
                <Col sm={9} className={style.highlightContent}>
                  <span>HIGHLIGHT</span>
                  <strong>{verbalHighlight.name}</strong>
                  <span>
                    You answered {verbalHighlight.percentCorrect}% of these question types correctly. While
                    you focus on other areas of improvement, make sure to keep reviewing this area so it
                    remains a strength.
                  </span>
                </Col>
              </Row>
            </div>
            {!shouldShowCTA ? (
              <div>
                <Row>
                  <Col sm={3} className="text-center">
                    <WateringSeedlingIcon style={{ fill: '#26B67C' }} height={150} width={100} />
                  </Col>
                  <Col sm={9} className={style.highlightContent}>
                    <span>ROOM FOR IMPROVEMENT</span>
                    <strong>{verbalImprovement.name}</strong>
                    <span>
                      You answered {verbalImprovement.percentCorrect}% of these questions correctly. Focusing
                      on this question type will give you the best opportunity to improve your score.
                    </span>
                  </Col>
                </Row>
              </div>
            ) : (
              renderStudyPlanCTA('verbal')
            )}
          </div>
        )}

        <h4>
          <strong>Your score breakdown</strong>
        </h4>
        <Row>
          <Col md={3} sm={12}>
            <HighSignificanceIcon style={{ fill: style.highSignificance, height: '17px', width: '22px' }} />
            <h5>
              <strong>High Significance</strong>
            </h5>
            <p className="text-gray-light">
              These question types are highly important to excel in to maximize your score.
            </p>
            {viewAnswersLink}
          </Col>
          <Col md={9} sm={12}>
            {verbalTags
              .filter((tag) => tag.significanceBucket === 'high')
              .map((tag) => renderSubjectTagRow(tag, style.highSignificance))}
          </Col>
        </Row>
        <hr />
        <Row>
          <Col md={3} sm={12}>
            <ModerateSignificanceIcon
              style={{ fill: style.moderateSignificance, height: '17px', width: '22px' }}
            />
            <h5>
              <strong>Moderate Significance</strong>
            </h5>
            <p className="text-gray-light">
              These question types are moderately important to excel in to maximize your score.
            </p>
            {viewAnswersLink}
          </Col>
          <Col md={9} sm={12}>
            {verbalTags
              .filter((tag) => tag.significanceBucket === 'moderate')
              .map((tag) => renderSubjectTagRow(tag, style.moderateSignificance))}
          </Col>
        </Row>
        <hr />
        <Row>
          <Col md={3} sm={12}>
            <LowSignificanceIcon style={{ fill: style.lowSignificance, height: '17px', width: '22px' }} />
            <h5>
              <strong>Low Significance</strong>
            </h5>
            <p className="text-gray-light">
              These question types are the least important to excel in to maximize your score.
            </p>
            {viewAnswersLink}
          </Col>
          <Col md={9} sm={12}>
            {verbalTags
              .filter((tag) => tag.significanceBucket === 'low')
              .map((tag) => renderSubjectTagRow(tag, style.lowSignificance))}
          </Col>
        </Row>
      </React.Fragment>
    );
  };

  const renderStudyPlanCTA = (examSection: string) => {
    return (
      <div>
        <Row>
          <Col sm={3} className="text-center u-margin-V-l">
            <HandsHoldingPlantIcon fill={style.brandPrimary} />
          </Col>
          <Col sm={9} className={style.highlightContent}>
            <span>NEXT STEPS</span>
            <strong>Your Study Plan is Ready!</strong>
            <span>
              Based on your {examSection} results, we've created your study plan focused on <span dangerouslySetInnerHTML={{__html: toSentence(props.resultingStudyPlanFocus[examSection], 'and', (item) => `<u>${item}</u>`)}} />.
            </span>
            <br />
            <div>
              <PrimaryButton href={paths.dashboard()}>
                View Your Study Plan
              </PrimaryButton>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <React.Fragment>
      {props.examSections.hasOwnProperty('math') && (
        <>
          <div>{renderMath()}</div>
          <hr className="u-margin-V-xl" />
        </>
      )}
      {props.examSections.hasOwnProperty('verbal') && (
        <>
          <div>{renderVerbal()}</div>
          <hr className="u-margin-V-xl" />
        </>
      )}

      <h3>
        <strong>Next Steps</strong>
      </h3>
      <div className={cx(style.cardRow, style.nextSteps)}>
        <div>
          <MagnifyingGlassIcon />
          <strong>Review Your Answers</strong>
          <small style={{ color: '#817E85' }}>Highly recommended</small>
          <p style={{ color: '#4E4B50' }}>
            See the questions you missed and review explanations to learn how to answer a similar
            problem correctly in the future.
          </p>
          <DefaultButton onClick={props.onClickAnswers}>Review Answers</DefaultButton>
        </div>
        <div>
          <LightBulbIcon />
          <strong>Learn How to Overcome Test Day Anxiety</strong>
          <small style={{ color: '#817E85' }}>9 minute read</small>
          <p style={{ color: '#4E4B50' }}>
            Feeling stressed about your exam? Try the techniques in this article to reduce your test anxiety and boost peace of mind!
          </p>
          <DefaultButton href="https://magoosh.com/gre/gre-test-day-anxiety/">
            Read Article
          </DefaultButton>
        </div>
        <div>
          <StartLessonsIcon />
          <strong>Continue Your Studies</strong>
          <small style={{ color: '#817E85' }}>
            Last session {moment(props.lastActivityAt * 1000).fromNow()}
          </small>
          <p style={{ color: '#4E4B50' }}>
            When you’re ready to jump back in, go to your Dashboard.
            <br />
            <br />
            <br />
          </p>
          <DefaultButton href={paths.dashboard()}>Go to Dashboard</DefaultButton>
        </div>
      </div>
    </React.Fragment>
  );
};

const renderSubjectTagRow = (tag: SubjectTagStat, color: string) => {
  const tagNameStyle = tag.totalAnswerCount === 0 ? { color: '#817E85' } : {};
  const progressBarColor = tag.totalAnswerCount === 0 || tag.correctAnswerCount === 0 ? '#EEEEEF' : color;
  const progressLabelColor =
    tag.totalAnswerCount === 0 || tag.correctAnswerCount === 0 ? '#817E85' : '#FFFFFF';
  const popOverText =
    tag.totalAnswerCount === 0
      ? "You didn't answer any questions for this subject"
      : `You answered ${tag.correctAnswerCount} questions correctly out of ${tag.totalAnswerCount}`;
  return (
    <div className="row u-margin-V-xs" key={tag.name}>
      <div className={cx('col-sm-3', style.subjectTagLabel)} style={tagNameStyle}>
        {tag.name}
      </div>
      <div className="col-sm-9">
        <ProgressBar
          color={progressBarColor}
          backgroundColor="#EEEEEF"
          percentage={tag.percentCorrect === 0 ? 100 : tag.percentCorrect} // Hack: Improper border radius when % is 0 so use 100 w/ the same bg color
          size="lg"
          minLabel={tag.totalAnswerCount === 0 ? 'N/A' : `${tag.percentCorrect}%`}
          minLabelStyle={{
            display: 'flex',
            alignSelf: 'flex-start',
            paddingLeft: '10px',
            color: progressLabelColor
          }}
          popoverText={popOverText}
        />
      </div>
    </div>
  );
};

const NoPerformanceData = () => (
  <div className={style.noPerformanceData}>
    <MagnifyingGlassIcon />
    <div>
      <strong>No Performance Data to Display</strong>
      <span>You didn’t answer enough questions on this section to assess your performance.</span>
    </div>
  </div>
);

export default PerformanceTabGre;
